import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "./ooraInfraDataset.css";
import toast from "react-hot-toast";
import {
  getOoraInfraDatasetList,
  removeOoraInfraDataset,
} from "../../Redux/Actions/ooraInfraDatasetActions";
import { formatDate } from "../../Utils/ooraInfraDatasetHelpers";
import OoraSideNav from "../Oora Sidebar/ooraSideNav";
import "../Oora Sidebar/ooraSideNav.css";

const LIMIT = 10;

const OoraInfraDataset = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { ooraInfraDatasetList, listLoading, removeLoading } = useSelector(
    (state) => state.ooraInfraDataset || {}
  );

  const list = ooraInfraDatasetList?.data?.list || [];
  const total = ooraInfraDatasetList?.data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [deleteRow, setDeleteRow] = useState(null);

  const fetchList = useCallback(() => {
    dispatch(getOoraInfraDatasetList({ page, limit: LIMIT, search }));
  }, [dispatch, page, search]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleRemove = async () => {
    const response = await dispatch(removeOoraInfraDataset({ id: deleteRow._id }));
    if (response?.statusCode === 200) {
      toast.success("Dataset removed successfully");
      setDeleteRow(null);
      if (list.length === 1 && page > 1) setPage((p) => p - 1);
      else fetchList();
    } else {
      toast.error(response?.message || "Something went wrong");
    }
  };

  // open file in a new tab instead of downloading
  const openDocument = async (e, url) => {
    e.preventDefault();
    const win = window.open("", "_blank");
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const isPdf = url.toLowerCase().split("?")[0].endsWith(".pdf");
      const objUrl = URL.createObjectURL(new Blob([blob], { type: isPdf ? "application/pdf" : blob.type }));
      win.location.href = objUrl;
    } catch (error) {
      win.location.href = url; // fallback (CORS blocked)
    }
  };

  return (
    <>
      <div className="oora-layout">
        <OoraSideNav />
        <div className="oora-layout-main">
          <div className="oid-wrapper">
            <div className="oid-header">
              <div className="oid-header-left">
                <div className="oid-header-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <ellipse cx="12" cy="5" rx="9" ry="3" />
                    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
                    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
                  </svg>
                </div>
                <div>
                  <h1 className="oid-title">Oora Infra Dataset</h1>
                  <p className="oid-subtitle">Manage infrastructure documents by location</p>
                </div>
              </div>
              <button
                type="button"
                className="oid-btn oid-btn--submit"
                onClick={() => navigate("/oora-infra-dataset/add")}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Dataset
              </button>
            </div>

            <div className="oid-card">
              <div className="oid-toolbar">
                <div className="oid-search">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search by title, description, methodology, note or document"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
                <span className="oid-count">{total} record{total === 1 ? "" : "s"}</span>
              </div>

              <div className="oid-table-wrap">
                <table className="oid-table">
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}>#</th>
                      <th>Title</th>
                      <th style={{ width: 90 }}>Year</th>
                      <th>Documents</th>
                      <th style={{ width: 130 }}>Created</th>
                      <th style={{ width: 130 }}>Updated</th>
                      <th style={{ width: 140, textAlign: "right" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listLoading ? (
                      <tr><td colSpan={7} className="oid-empty">Loading...</td></tr>
                    ) : list.length === 0 ? (
                      <tr><td colSpan={7} className="oid-empty">No datasets found</td></tr>
                    ) : (
                      list.map((row, i) => (
                        <tr key={row._id}>
                          <td className="oid-mono">{(page - 1) * LIMIT + i + 1}</td>
                          <td className="oid-row-title">{row.title}</td>
                          <td>{row.year || "-"}</td>
                          <td>
                            <div className="oid-chips">
                              {row.documents?.slice(0, 2).map((d) => (
                                <a
                                  key={d._id}
                                  href={d.url}
                                  onClick={(e) => openDocument(e, d.url)}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="oid-chip"
                                  title={d.description || d.title}
                                >
                                  {d.title}
                                </a>
                              ))}
                              {row.documents?.length > 2 && (
                                <span
                                  className="oid-chip oid-chip--more"
                                  title={row.documents.slice(2).map((d) => d.title).join(", ")}
                                >
                                  +{row.documents.length - 2} more
                                </span>
                              )}
                            </div>
                          </td>
                          <td>{formatDate(row.created_at)}</td>
                          <td>{formatDate(row.updated_at)}</td>
                          <td style={{ textAlign: "right" }}>
                            <div className="oid-actions">
                              <button
                                type="button"
                                className="oid-icon-btn"
                                title="View"
                                onClick={() => navigate(`/oora-infra-dataset/view/${row._id}`)}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                  <circle cx="12" cy="12" r="3" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                className="oid-icon-btn"
                                title="Edit"
                                onClick={() =>
                                  navigate(`/oora-infra-dataset/update/${row._id}`, { state: { row } })
                                }
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M12 20h9" />
                                  <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                className="oid-icon-btn oid-icon-btn--danger"
                                title="Remove"
                                onClick={() => setDeleteRow(row)}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                  <path d="M10 11v6M14 11v6" />
                                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="oid-pagination">
                <span className="oid-page-info">Page {page} of {totalPages}</span>
                <div className="oid-page-btns">
                  <button
                    type="button"
                    className="oid-btn oid-btn--ghost"
                    disabled={page <= 1 || listLoading}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="oid-btn oid-btn--ghost"
                    disabled={page >= totalPages || listLoading}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>

          {deleteRow && (
            <div className="oid-overlay" onMouseDown={() => !removeLoading && setDeleteRow(null)}>
              <div className="oid-modal oid-modal--sm" onMouseDown={(e) => e.stopPropagation()}>
                <div className="oid-modal-head">
                  <h2 className="oid-section-title">Remove Dataset</h2>
                </div>
                <div className="oid-modal-body">
                  <p className="oid-confirm-text">
                    Are you sure you want to remove <strong>{deleteRow.title}</strong>? This cannot be undone.
                  </p>
                </div>
                <div className="oid-modal-foot">
                  <button
                    type="button"
                    className="oid-btn oid-btn--ghost"
                    onClick={() => setDeleteRow(null)}
                    disabled={removeLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="oid-btn oid-btn--danger"
                    onClick={handleRemove}
                    disabled={removeLoading}
                  >
                    {removeLoading ? "Removing..." : "Remove"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default OoraInfraDataset;